import React, { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import EmployeeAutocomplete, { EmployeeOption } from '../../common/EmployeeAutocomplete';
import { helpElement } from '../../../help/attrs';

/** Filter sentinel: records with no salesperson. */
export const UNASSIGNED_ID = '__unassigned__';

interface Props {
  /** Selected salesperson id (or UNASSIGNED_ID in a filter). */
  value: string | null;
  /** Display name of the selected person (the lists and forms already carry it). */
  valueName?: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  /** Filter mode adds an "Unassigned" choice. */
  withUnassigned?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  disabled?: boolean;
  helperText?: string;
  'data-help'?: string;
}

const UNASSIGNED_OPTION: EmployeeOption = { id: UNASSIGNED_ID, username: 'Unassigned', fullName: 'Unassigned' };

/** S0: the one salesperson picker (filters, forms, reassign dialog). Built on the shared employee autocomplete. */
const SalespersonSelect: React.FC<Props> = ({
  value, valueName, onChange, label = 'Salesperson', withUnassigned = false, size = 'small', sx, disabled, helperText, 'data-help': dataHelp,
}) => {
  const selected = useMemo<EmployeeOption | null>(() => {
    if (!value) return null;
    if (value === UNASSIGNED_ID) return UNASSIGNED_OPTION;
    return { id: value, username: valueName ?? value, fullName: valueName ?? undefined };
  }, [value, valueName]);
  // The filter (withUnassigned) is pointless, and its employee list forbidden, for a caller with no scope key.
  const scopeTeam = usePermission(PERMISSIONS.SALES_SCOPE_TEAM);
  const scopeDept = usePermission(PERMISSIONS.SALES_SCOPE_DEPARTMENT);
  const scopeAll = usePermission(PERMISSIONS.SALES_SCOPE_ALL);
  const extra = useMemo(() => (withUnassigned ? [UNASSIGNED_OPTION] : undefined), [withUnassigned]);
  if (withUnassigned && !(scopeTeam || scopeDept || scopeAll)) return null;
  return (
    <EmployeeAutocomplete
      label={label} size={size} sx={sx} disabled={disabled} helperText={helperText} activeOnly {...helpElement(dataHelp)}
      value={selected} extraOptions={extra} onChange={o => onChange(o ? o.id : null)}
    />
  );
};

/** Query params for a salesperson filter value. */
export const salespersonParams = (value: string | null): { salespersonId?: string; unassigned?: boolean } => {
  if (!value) return {};
  return value === UNASSIGNED_ID ? { unassigned: true } : { salespersonId: value };
};

export default SalespersonSelect;
